import React from 'react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { colorUtils } from '../../theme';

export interface PieChartDataItem {
    name: string;
    value: number;
    color?: string;
    [key: string]: any;
}

interface PieChartProps {
    data: PieChartDataItem[];
    title?: string;
    subtitle?: string;
    height?: number;
    showLegend?: boolean;
    showTooltip?: boolean;
    showCenterValue?: boolean;
    centerLabel?: string;
    variant?: 'pie' | 'donut';
    colors?: string[];
    onSegmentClick?: (data: PieChartDataItem, index: number) => void;
}

// Default color palette for cybersecurity/SOCmate theme - using OKLCH
const DEFAULT_COLORS = [
    'oklch(62% 0.22 25)',  // red - critical
    'oklch(72% 0.18 50)',  // orange - high
    'oklch(85% 0.16 95)',  // yellow - medium
    'oklch(65% 0.18 150)', // green - low
    'oklch(60% 0.18 260)', // blue - info
    'oklch(60% 0.22 291)', // purple
    'oklch(65% 0.20 350)', // pink
    'oklch(65% 0.14 180)', // teal
    'oklch(75% 0.18 75)',  // amber
    'oklch(70% 0.15 200)', // cyan
];

export const PieChart: React.FC<PieChartProps> = ({
                                                      data,
                                                      title,
                                                      subtitle,
                                                      height = 300,
                                                      showLegend = true,
                                                      showTooltip = true,
                                                      showCenterValue = true,
                                                      centerLabel,
                                                      variant = 'donut',
                                                      colors = DEFAULT_COLORS,
                                                      onSegmentClick,
                                                  }) => {
    const theme = useTheme();
    const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

    // Calculate total for center value
    const total = data.reduce((sum, item) => sum + item.value, 0);

    // Custom label renderer for the center value
    const renderCenterLabel = () => {
        if (!showCenterValue || variant === 'pie') return null;

        return (
            <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                    fontSize: '22px',
                    fontWeight: 700,
                    fill: theme.palette.text.primary,
                }}
            >
                <tspan x="50%" dy="-0.2em">
                    {total.toLocaleString()}
                </tspan>
            </text>
        );
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || !payload.length) return null;

        const data = payload[0].payload;
        const percentage = ((data.value / total) * 100).toFixed(1);

        return (
            <Paper
                elevation={8}
                sx={{
                    p: 1.5,
                    backgroundColor: colorUtils.withOpacity(theme.palette.background.paper, 0.95),
                    border: `1px solid ${colorUtils.withOpacity(theme.palette.primary.main, 0.3)}`,
                    backdropFilter: 'blur(10px)',
                }}
            >
                <Typography variant="body2" fontWeight={600} gutterBottom>
                    {data.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {data.value.toLocaleString()} ({percentage}%)
                </Typography>
            </Paper>
        );
    };

    // Custom legend
    const CustomLegend = ({ payload }: any) => {
        if (!showLegend) return null;

        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: 3,
                    mt: 2,
                }}
            >
                {payload.map((entry: any, index: number) => {
                    // Calculate percentage, ensuring total is not zero to avoid division by zero
                    const percentage = total === 0 ? 0 : ((entry.payload?.value / total) * 100).toFixed(1);

                    return (
                        <Box
                            key={`legend-${index}`}
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: '100px', // Increased width for better display
                            }}
                        >
                            <Box
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: entry.color,
                                }}
                            />
                            {/* 1. Raw Value (Count) */}
                            <Typography
                                variant="caption"
                                fontWeight={600} // Bolder font weight for the key data point (Value)
                                sx={{ fontSize: '0.85rem', color: theme.palette.text.primary }}
                            >
                                {entry.value.toLocaleString()}
                            </Typography>

                            {/* 2. Percentage */}
                            <Typography
                                variant="caption"
                                color="text.secondary" // Lighter color for secondary info (Percentage)
                                sx={{ fontSize: '0.85rem' }}
                            >
                                ({percentage}%)
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box sx={{ overflow: 'visible' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                {(title || subtitle) && (
                    <Box>
                        {title && (
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                {title}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography variant="body2" color="text.secondary">
                                {total.toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
            <ResponsiveContainer width="100%" height={height + 40}>
                <RechartsPieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={height / 2.8}
                        innerRadius={variant === 'donut' ? height / 4.2 : 0}
                        fill={theme.palette.primary.main}
                        dataKey="value"
                        paddingAngle={2}
                        cornerRadius={4}
                        onMouseEnter={(_, index) => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                        onClick={(data, index) => {
                            if (onSegmentClick) {
                                onSegmentClick(data, index);
                            }
                        }}
                        style={{ cursor: onSegmentClick ? 'pointer' : 'default' }}
                    >
                        {data.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={entry.color || colors[index % colors.length]}
                                stroke="none"
                                style={{
                                    filter: hoveredIndex === index ? 'brightness(1.15)' : 'none',
                                    transition: 'filter 0.2s ease',
                                }}
                            />
                        ))}
                    </Pie>
                    {showTooltip && <Tooltip content={<CustomTooltip />} />}
                    {showLegend && (
                        <Legend
                            content={<CustomLegend />}
                            onMouseEnter={() => {}}
                            onMouseLeave={() => {}}
                        />
                    )}
                    {renderCenterLabel()}
                </RechartsPieChart>
            </ResponsiveContainer>

            {/* Add CSS for hover scale effect */}
            <style>{`
                .recharts-pie-sector:hover {
                    transform: scale(1.05);
                    transform-origin: center;
                    transition: transform 0.2s ease;
                }
            `}</style>
        </Box>
    );
};

export default PieChart;